import { Component, h, Listen, Event, EventEmitter, Prop, State } from '@stencil/core';
import { Customer } from '../../../globals/customer';
import { getCardType, objectHasValues, ErrorHelper } from '../../../helpers';
import { updateCardInfo } from '../../../utils';
import { CardInfoTranslation } from '../../../globals/translations';
import { Address } from '../../../globals/address';

@Component({
  tag: 'card-info-state',
  styleUrl: './state-styles/card-info-state.scss',
  shadow: false,
})
export class CardInfoState {
  @State() formattedBilling;
  @State() isLoading = false;
  @State() prohibitedStatesCard: string[];

  @Prop({ mutable: true }) cardInfo: Customer['cardInfo'];
  @Prop({ mutable: true }) addressInfo: Customer['address'];
  @Prop({ mutable: true }) errors: ErrorHelper;
  @Prop() blockedStatesCardInfo: string[];
  @Prop() translation: Partial<CardInfoTranslation>;

  @Event() updateCustomer: EventEmitter<Partial<Customer>>;
  @Event() continueClick: EventEmitter;

  componentWillLoad() {
    const {
      billing: { line1, line2, city, state, zip },
    } = this.addressInfo;

    this.formattedBilling = { billingLine1: line1, billingLine2: line2, billingCity: city, billingState: state, billingZip: zip };
  }

  @Listen('updateCardInfo', { target: 'body' })
  getUpdatedCardInfo(event: CustomEvent) {
    if (event.detail) {
      const { cardNumber, cardExpDate, cardCvv, cardFirstName, cardLastName } = event.detail;

      if (cardNumber !== this.cardInfo.cardNumber) {
        this.cardInfo.cardNumber = cardNumber;
      }

      if (cardExpDate !== this.cardInfo.expirationDate) {
        this.cardInfo.expirationDate = cardExpDate;
      }

      if (cardFirstName !== this.cardInfo.firstName) {
        this.cardInfo.firstName = cardFirstName;
      }

      if (cardLastName !== this.cardInfo.lastName) {
        this.cardInfo.lastName = cardLastName;
      }

      if (cardCvv !== this.cardInfo.cvv) {
        this.cardInfo.cvv = cardCvv;
      }

      this.cardInfo.cardType = getCardType(cardNumber);

      if (objectHasValues(this.cardInfo)) {
        this.errors.resetErrorFlags();
      }
    }
  }

  @Listen('updateCardBillingInfo', { target: 'body' })
  async getUpdatedCardBillingInfo(event: CustomEvent) {
    if (event.detail) {
      const { billingStreetAddress1, billingStreetAddress2, billingCity, billingState, billingZipCode } = event.detail;

      const toUpdate = {
        line1: billingStreetAddress1,
        line2: billingStreetAddress2,
        city: billingCity,
        state: billingState,
        zip: billingZipCode,
      } as Address;

      if (Boolean(Object.keys(toUpdate).length) && objectHasValues(this.cardInfo)) {
        const [month, year] = this.cardInfo.expirationDate.split('/');
        const formattedExpDate = `${month}/20${year}`;

        const newFields = { address: { shipping: this.addressInfo.shipping, billing: { ...toUpdate } }, cardInfo: this.cardInfo };
        const apiFields = { billingAddress: { ...toUpdate }, ...this.cardInfo, expirationDate: formattedExpDate };

        this.isLoading = true;

        try {
          await updateCardInfo({ ...apiFields });
          this.updateCustomer.emit(newFields);
          this.continueClick.emit();
        } catch {
          this.errors.hasGenericError = true;
        }
        this.isLoading = false;
      }
    }
  }

  render() {
    const cardInfoProps = { ...this.cardInfo, ...this.addressInfo.shipping, ...this.formattedBilling, translation: this.translation };

    return (
      <pc-card-info {...cardInfoProps} isLoading={this.isLoading} prohibitedStatesCard={this.blockedStatesCardInfo}>
        {this.errors.hasGenericError && <pc-generic-error slot="error-card" />}
      </pc-card-info>
    );
  }
}
